import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import Toggle from 'components/Form/Toggle';
import ModalPreanalise from 'components/Modal/ModalPreanalise';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GET } from 'services/api';
import { Icon, StatusContent, Title } from 'ui/styled';
import { selectColors } from 'utils';
import { parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';
import {
  FinancingProposalFormsWrapper,
  FinancingProposalHeader,
} from './styled';

export default function FinancingTablePreanalise() {
  const { id, opcao } = useParams();
  const history = useHistory();
  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalData, setModalData] = useState(null);
  const { setModal } = useContext(CoreContext);
  const normalizeProposta = (p) => ({
    id: p.id,
    banco: p.banco || null,
    bancos: p.bancos || [],
    valor_financiamento: p.valor_financiamento,
    valor_entrada: p.valor_entrada,
    prazo_financiamento: p.prazo_financiamento,
    valor_parcelas: p.valor_parcelas,
    data: p.data,
    createdAt: p.createdAt,
    status: p.status,
    aceito: p.aceito || false,
  });
  const init = useCallback(async () => {
    if (!id) {
      toast.error('ID da proposta não informado');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const populate = 'distribuidor,integrador,banco,uf,bancos.banco';
      const url = `/pre-proposals/${id}?populate=${populate}`;
      const propostaResult = await GET(url, true);
      if (!propostaResult) {
        toast.error('Pré-proposta não encontrada');
        setPropostas([]);
        return;
      }
      const normalized = normalizeProposta(propostaResult);
      setPropostas([normalized]);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      toast.error(err?.message || 'Erro ao carregar informações');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => init(), [init]);

  // Abre o modal apenas se o status permitir edição
  const openModal = useCallback(
    (dadosDoBanco) => {
      const status = dadosDoBanco.status?.toLowerCase();
      if (status === 'aprovado' || status === 'recusado') {
        toast.warn('Propostas aprovadas ou recusadas não podem ser editadas.');
        return;
      }
      setModalData({
        dados: dadosDoBanco,
        preProposalId: Number(id),
        reload: init,
      });
    },
    [id, init]
  );
  const closeModal = () => setModalData(null);
  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Data',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.createdAt),
      },
      {
        title: 'Banco',
        renderCell: ({ row }) =>
          normalizeName(row.banco?.nome_fantasia) ||
          normalizeName(row.banco?.razao_social) ||
          '-',
      },
      {
        title: 'Valor Venda',
        renderCell: ({ row }) => parseCurrency(row.valor_venda),
      },
      {
        title: 'Valor Financiado',
        renderCell: ({ row }) => parseCurrency(row.valor_financiamento),
      },
      {
        title: 'Entrada',
        renderCell: ({ row }) => parseCurrency(row.valor_entrada),
      },
      {
        title: 'Prazo',
        renderCell: ({ row }) => row.prazo_financiamento || '-',
      },
      {
        title: 'PMT',
        renderCell: ({ row }) => parseCurrency(row.valor_parcelas),
      },
      {
        title: '1º Vencimento',
        renderCell: ({ row }) =>
          !row.data ? '--' : formatadores.TIMESTAMP_TO_BR(row.data),
      },
      {
        title: 'TAG',
        renderCell: ({ row }) => (
          <StatusContent
            color={selectColors(
              row.status === 'pre aprovado' ? 'Aprovado' : row.status
            )}
          >
            {normalizeName(row.status) || ''}
          </StatusContent>
        ),
      },
      ...(opcao === 'preaprovado'
        ? [
            {
              title: 'Aceito',
              ref: 'aceito',
              renderCell: ({ row }) => (
                <Toggle
                  onChange={() => {
                    // Se nao esta aprovado
                    if (row.status !== 'Pré Aprovado') {
                      toast.error(
                        'Somente propostas aprovadas podem ser aceitas'
                      );
                      return;
                    }
                    // Se já foi aceito nao faz nada
                    if (row.aceito) {
                      toast.info('Esta proposta já foi aceita');
                      return;
                    }
                    // Abre modal ou realiza ação
                    setModal({
                      type: 'sample',
                      origem: 'pre-proposta',
                      banco: row,
                      id: id,
                      row,
                      reload: () => init(),
                    });
                  }}
                  checked={row.aceito}
                  label=""
                  active={true}
                  value={true}
                  disabled={row.aceito} //desativa o toggle se já aceito
                />
              ),
            },
          ]
        : []),
      {
        title: '',
        renderCell: ({ row }) => {
          const status = row.status?.toLowerCase();
          let isBlocked;
          if (opcao === 'preaprovado' && status === 'não consultado') {
            isBlocked = false;
          } else {
            isBlocked = status === 'pré aprovado' || status === 'recusado';
          }
          return (
            <Icon
              icon="arrow-right"
              style={{
                opacity: isBlocked ? 0.4 : 1,
                cursor: isBlocked ? 'not-allowed' : 'pointer',
              }}
              title={
                isBlocked
                  ? status === 'pré aprovado'
                    ? 'Esta proposta já foi aprovada e não pode ser editada'
                    : 'Esta proposta não pode ser editada'
                  : 'Editar proposta'
              }
              onClick={() => {
                if (isBlocked) {
                  toast.error(
                    status === 'pré aprovado'
                      ? 'Esta proposta já foi aprovada e não pode ser mais editada.'
                      : 'Esta proposta não pode ser mais editada.'
                  );
                  return;
                }
                openModal(row);
              }}
            />
          );
        },
      },
    ];
    return cols;
  }, [setModal, init, id, opcao, openModal]);
  const filteredRows = useMemo(
    () =>
      propostas
        .flatMap((proposta) =>
          proposta.bancos.map((itemBanco) => ({
            id: itemBanco.id,
            banco: itemBanco.banco,
            valor_venda: proposta.valor_financiamento,
            valor_financiamento: itemBanco.valor_financiamento,
            valor_entrada: itemBanco.valor_entrada,
            prazo_financiamento: itemBanco.prazo_financiamento,
            valor_parcelas: itemBanco.valor_parcelas,
            data: itemBanco.data,
            status:
              itemBanco.status === 'aprovado'
                ? 'Pré Aprovado'
                : itemBanco.status === 'pendente'
                  ? 'Não Consultado'
                  : normalizeName(itemBanco.status),
            aceito: itemBanco.aceito,
            createdAt: proposta.createdAt,
          }))
        )
        .filter((r) =>
          [
            'em analise',
            'pré aprovado',
            'recusado',
            'pendente',
            'não consultado',
          ].includes(r.status?.toLowerCase())
        ),
    [propostas]
  );

  return (
    <ContainerAuthenticated>
      <Title>Propostas em Pré Análise</Title>
      <FinancingProposalFormsWrapper>
        {/* Cabeçalho com título à esquerda + botão Voltar à direita */}
        <FinancingProposalHeader
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <Title small nomargin>
            Propostas Bancárias
          </Title>
          <Button
            color="blue"
            width="fit-content"
            small
            nospace
            onClick={() => history.goBack()}
          >
            Voltar
          </Button>
        </FinancingProposalHeader>
        {/* Tabela */}
        <BasicTable
          columns={columns}
          rows={filteredRows}
          loading={loading}
          pagination={filteredRows.length > 0}
          noDataMessage={
            filteredRows.length === 0
              ? 'Nenhuma proposta encontrada'
              : undefined
          }
        />
      </FinancingProposalFormsWrapper>
      {/* Modal */}
      {modalData && (
        <ModalPreanalise
          dados={modalData.dados}
          preProposalId={modalData.preProposalId}
          reload={modalData.reload}
          onClose={closeModal}
        />
      )}
    </ContainerAuthenticated>
  );
}
